import React from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { OpenInNew } from '@mui/icons-material';

import PersonPhoto from '../people/PersonPhoto';
import { ToneChip } from '../common/StatusChip';
import type { Person } from '../../services/peopleService';
import { PERSON_KIND_LABEL, PERSON_KIND_TONE } from './familyFormat';

interface Props {
  person: Pick<Person, '_id' | 'name' | 'grNumber' | 'kind' | 'photoFilename'>;
  /** Identifiers and placement, joined under the name ("GR 000035 · Class 8 · A"). */
  details?: Array<string | null | undefined | false>;
  /** Replaces the student / applicant / alumni chip. */
  chip?: React.ReactNode;
  /** Shows the open-profile button. */
  onOpen?: () => void;
}

/** One child of a household — in the family drawer and wherever siblings are listed. */
const FamilyChildRow: React.FC<Props> = ({ person, details, chip, onOpen }) => {
  const caption = (details ?? [person.grNumber && `GR ${person.grNumber}`]).filter(Boolean).join('  ·  ');
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2, py: 1.25 }}>
      <PersonPhoto name={person.name} caption={person.grNumber && `GR ${person.grNumber}`} photoFilename={person.photoFilename} size={32} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{person.name}</Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary" noWrap component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {caption}
          </Typography>
        )}
      </Box>
      {chip ?? <ToneChip label={PERSON_KIND_LABEL[person.kind]} tone={PERSON_KIND_TONE[person.kind]} />}
      {onOpen && (
        <Tooltip title="Open profile">
          <IconButton size="small" onClick={onOpen} aria-label={`Open ${person.name}'s profile`}>
            <OpenInNew sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default React.memo(FamilyChildRow);
